import { Container, Carousel } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Footer } from '../../components/Footer/Footer';
import MarkdownFormat from '../../components/MarkdownFormat/MarkdownFormat';

export function TokenInfo({ appData }) {
  const navigate = useNavigate();
  const { selectedToken , selectedCollection} = appData;
  
  if (!selectedToken) {
    return (
      <main
        style={{
          minHeight: '100vh',
          background: 'var(--theme-background, #ffffff)',
          color: 'var(--theme-text, #212529)',
          padding: '60px 16px 100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <h1 style={{ fontSize: 'clamp(24px, 5vw, 32px)', marginBottom: '20px' }}>Token not found</h1>
          <button
            onClick={() => navigate(`/collection/${selectedCollection.publicId}`)}
            style={{
              marginTop: '20px',
              padding: '12px 24px',
              background: 'var(--theme-primary, #007bff)',
              color: '#ffffff',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              minHeight: '44px',
            }}
          >
            Back to Collection
          </button>
        </div>
      </main>
    );
  }

  return (
    <main
      style={{
        minHeight: '100vh',
        background: 'var(--theme-background, #ffffff)',
        color: 'var(--theme-text, #212529)',
        padding: '60px 0 100px',
        position: 'relative',
      }}
    >
      {/* Back Button */}
      <button
        onClick={() => navigate(`/collection/${selectedCollection.publicId}`)}
        style={{
          position: 'fixed',
          top: 12,
          left: 12,
          background: 'var(--theme-surface, #ffffff)',
          border: '1px solid var(--theme-border, #e9ecef)',
          borderRadius: 12,
          padding: '12px 14px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s ease',
          zIndex: 10,
          minWidth: '44px',
          minHeight: '44px',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
        }}
        aria-label="Go back to collection"
      >
        <ArrowLeft
          size={20}
          style={{
            color: 'var(--theme-text, #212529)',
          }}
        />
      </button>

      <Container style={{ maxWidth: 1200, padding: '0 16px' }}>
        {/* Header Section with Image */}
        <header
          style={{
            marginBottom: 32,
            textAlign: 'center',
          }}
        >
          <div
            className="token-image-container"
            style={{
              width: '100%',
              maxWidth: '700px',
              height: 'clamp(250px, 50vw, 400px)',
              margin: '0 auto 24px',
              borderRadius: 16,
              overflow: 'hidden',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
              background: 'var(--theme-surface, #f8f9fa)',
              position: 'relative',
            }}
          >
            {(() => {
              // Check if media exists and is an array with items
              let mediaImages = [];
              
              if (selectedToken.media && Array.isArray(selectedToken.media) && selectedToken.media.length > 0) {
                // Media already extracted as array of URLs
                mediaImages = selectedToken.media.filter(url => url && typeof url === 'string');
              } else if (selectedToken.mutableData && selectedToken.mutableData.userData) {
                // Try to extract from userData if not already extracted
                try {
                  const userData = JSON.parse(selectedToken.mutableData.userData);
                  
                  if (userData.media && Array.isArray(userData.media)) {
                    mediaImages = userData.media
                      .map(item => {
                        if (typeof item === 'string') {
                          return item;
                        } else if (item && typeof item === 'object' && item.url) {
                          return item.url;
                        }
                        return null;
                      })
                      .filter(url => url && typeof url === 'string');
                  }
                } catch (error) {
                  console.error('Error parsing userData for media:', error);
                }
              }
              
              // If multiple media images, use carousel
              if (mediaImages.length > 1) {
                return (
                  <Carousel
                    indicators={false}
                    controls={true}
                    style={{
                      width: '100%',
                      height: '100%',
                    }}
                    className="token-media-carousel"
                  >
                    {mediaImages.map((imageUrl, index) => (
                      <Carousel.Item key={index}>
                        <img
                          src={imageUrl}
                          alt={`Token ${index + 1}`}
                          style={{
                            width: '100%',
                            height: 'clamp(250px, 50vw, 400px)',
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                );
              }
              
              // If single media image, display it
              if (mediaImages.length === 1) {
                return (
                  <img
                    src={mediaImages[0]}
                    alt={selectedToken.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                );
              }
              
              // Fall back to tokenIcon
              return (
                <img
                  src={selectedToken.tokenIcon}
                  alt={selectedToken.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              );
            })()}
          </div>
          <h1
            style={{
              margin: 0,
              fontSize: 'clamp(24px, 5vw, 36px)',
              fontWeight: 700,
              letterSpacing: '-0.02em',
              color: 'var(--theme-text, #212529)',
              padding: '0 16px',
            }}
          >
            {selectedToken.name}
          </h1>
        </header>

        {/* Markdown Content Section */}
        <section aria-label="Token information">
          <MarkdownFormat content={selectedToken.markdown} />
        </section>
      </Container>
      <Footer />
    </main>
  );
}
